import { useState, useEffect, useRef } from "react";
import { Outlet, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useGuard } from "@/context/GuardContext";
import { useCreerRdv } from "@/components/CreerRdv";
import CommandPalette from "@/components/CommandPalette";
import api from "@/lib/api";
import { saveShortcutsLocal } from "@/lib/shortcuts";
import { APP_VERSION, APP_VERSION_LABEL } from "@/version";
import {
  Calendar, Users, MapPin, MessageSquare, BarChart3, Settings,
  LogOut, Plus, ChevronRight, Menu, Search,
} from "lucide-react";

const NAV = [
  { to: "/agenda", label: "Agenda", icon: Calendar },
  { to: "/patients", label: "Patients", icon: Users, tabs: [
    { to: "/patients", label: "Patientèle" },
    { to: "/patients/suivi", label: "Suivi" },
    { to: "/patients/soins", label: "Soins" },
  ] },
  { to: "/tournees", label: "Tournées de soin", icon: MapPin },
  { to: "/relation", label: "Relation client", icon: MessageSquare },
  { to: "/piloter", label: "Piloter", icon: BarChart3, tabs: [
    { to: "/piloter", label: "Résumé" },
    { to: "/piloter/paiements", label: "Paiements" },
    { to: "/piloter/exports", label: "Exports" },
  ] },
];

const TITLES = {
  "/agenda": "Agenda", "/patients": "Patients", "/prestations": "Prestations",
  "/tournees": "Tournées de soin",
  "/relation": "Relation client", "/piloter": "Piloter", "/parametres": "Paramètres",
};

export default function Layout() {
  const { user, logout } = useAuth();
  const { attempt } = useGuard();
  const { openCreerRdv } = useCreerRdv();
  const nav = useNavigate();
  const loc = useLocation();
  const [open, setOpen] = useState(false);
  const [psName, setPsName] = useState("");
  const [crumb, setCrumb] = useState(null);
  const [today, setToday] = useState([]);
  const headerRef = useRef(null);

  const go = (to) => { setOpen(false); attempt(to); };

  // Mesure dynamique de la hauteur de l'en-tête → variable CSS --header-h (volets flottants sticky)
  useEffect(() => {
    const el = headerRef.current;
    if (!el) return;
    const set = () => document.documentElement.style.setProperty("--header-h", `${el.offsetHeight}px`);
    set();
    const ro = new ResizeObserver(set);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  useEffect(() => {
    api.get("/settings").then((r) => { setPsName(r.data?.ps_nom || ""); if (r.data?.raccourcis) saveShortcutsLocal(r.data.raccourcis); }).catch(() => {});
    const loadToday = () => {
      const s = new Date(); s.setHours(0, 0, 0, 0);
      const e = new Date(s); e.setDate(e.getDate() + 1);
      api.get("/rdvs", { params: { start: s.toISOString(), end: e.toISOString() } })
        .then((r) => setToday((r.data || []).filter((x) => x.type_rdv !== "absence" && x.statut !== "Annulé").sort((a, b) => new Date(a.date) - new Date(b.date))))
        .catch(() => {});
    };
    loadToday();
    const onChange = () => loadToday();
    window.addEventListener("rdv-created", onChange);
    window.addEventListener("rdv-changed", onChange);
    const iv = setInterval(loadToday, 60000);
    return () => { window.removeEventListener("rdv-created", onChange); window.removeEventListener("rdv-changed", onChange); clearInterval(iv); };
  }, []);

  useEffect(() => {
    const s = loc.pathname.split("/").filter(Boolean);
    const b = "/" + (s[0] || "");
    if (b === "/patients" && s[1] && s[1] !== "soins") {
      api.get(`/patients/${s[1]}`).then((r) => setCrumb({ patient: `${r.data.prenom || ""} ${r.data.nom || ""}`.trim() })).catch(() => setCrumb(null));
    } else if (b === "/fiche-soin" && s[1]) {
      api.get(`/soins/${s[1]}`).then(async (r) => {
        let pname = "";
        if (r.data.patient_id) { try { const pr = await api.get(`/patients/${r.data.patient_id}`); pname = `${pr.data.prenom || ""} ${pr.data.nom || ""}`.trim(); } catch (_) { /* noop */ } }
        setCrumb({ patient: pname, patientId: r.data.patient_id, titre: (r.data.titre || "").trim() });
      }).catch(() => setCrumb(null));
    } else setCrumb(null);
  }, [loc.pathname]);

  const seg = loc.pathname.split("/").filter(Boolean);
  const base = "/" + (seg[0] || "agenda");
  const activeBase = base === "/fiche-soin" ? "/patients" : base;

  let trail;
  if (base === "/patients" && seg[1] === "soins") {
    trail = [{ label: "Patients", to: "/patients" }, { label: "Soins" }];
  } else if (base === "/patients" && seg[1]) {
    trail = [{ label: "Patients", to: "/patients" }, { label: crumb?.patient || "Fiche patient" }];
  } else if (base === "/fiche-soin" && seg[1]) {
    trail = [
      { label: "Patients", to: "/patients" },
      { label: crumb?.patient || "Patient", to: crumb?.patientId ? `/patients/${crumb.patientId}` : undefined },
      { label: crumb?.titre || "Soin" },
    ];
  } else if (base === "/tournees" && seg[1]) {
    trail = [{ label: "Tournées de soin", to: "/tournees" }, { label: `Tournée ${seg[1]}` }];
  } else if (base === "/piloter") {
    const sub = { paiements: "Paiements", exports: "Exports" }[seg[1]] || "Résumé";
    trail = [{ label: "Piloter", to: "/piloter" }, { label: sub }];
  } else {
    trail = [{ label: TITLES[base] || "Soin xPower", to: base }];
  }
  let title = trail[trail.length - 1].label;
  if (base === "/fiche-soin" && seg[1]) title = "Soin";
  else if (base === "/patients" && seg[1] && seg[1] !== "soins") title = "Patient";

  const activeNav = NAV.find((n) => n.to === activeBase);
  const panelTabs = activeNav?.tabs;
  const tabActive = (t) => {
    if (activeBase === "/patients") {
      if (t.to === "/patients/soins") return loc.pathname === "/patients/soins";
      if (t.to === "/patients/suivi") return loc.pathname === "/patients/suivi";
      return loc.pathname === "/patients" || (loc.pathname.startsWith("/patients/") && !["/patients/soins", "/patients/suivi"].includes(loc.pathname));
    }
    return loc.pathname === t.to;
  };

  const now = new Date();
  const upcoming = today.find((r) => new Date(r.date) >= now) || today[0];
  const fmtTime = (d) => new Date(d).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" });
  const todayStr = now.toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long" });
  const initials = (psName || user?.name || user?.email || "?").trim().split(/\s+/).map((w) => w[0]).slice(0, 2).join("").toUpperCase();

  const SidebarInner = () => (
    <div className="flex flex-col h-full" style={{ padding: "18px 12px" }}>
      {/* Marque */}
      <div className="flex flex-col items-start gap-1.5 px-1 mb-5">
        <img src="/soin-xpower-logotype-blanc.png" alt="Soin xPower" data-testid="brand-logo" className="h-8 w-auto max-w-[180px] object-contain object-left" />
        <div className="text-[11.5px] truncate max-w-[180px]" style={{ color: "rgba(255,255,255,0.88)" }}>{psName || user?.name || "Praticien"}</div>
      </div>

      {/* Volets principaux */}
      <nav className="flex-1 space-y-1">
        {NAV.map(({ to, label, icon: Icon }) => {
          const active = activeBase === to;
          return (
            <button key={to} onClick={() => go(to)} data-testid={`nav-${to.slice(1)}`}
              className="w-full flex items-center gap-3 transition-colors text-[13.5px]"
              style={{
                padding: "9px 10px", borderRadius: "8px",
                background: active ? "var(--accent)" : "transparent",
                color: active ? "var(--ink)" : "var(--side-fg)",
                fontWeight: active ? 600 : 500,
              }}
              onMouseEnter={(e) => { if (!active) e.currentTarget.style.background = "var(--side-card)"; }}
              onMouseLeave={(e) => { if (!active) e.currentTarget.style.background = "transparent"; }}>
              <Icon className="w-4 h-4 shrink-0" />{label}
            </button>
          );
        })}
      </nav>

      {/* Carte Aujourd'hui */}
      <div data-testid="today-card" className="mt-4 mb-2" style={{ background: "var(--side-card)", borderRadius: "10px", padding: "12px" }}>
        <div className="text-[13px] font-medium">
          <span style={{ background: "linear-gradient(transparent 55%, var(--accent) 55%)", padding: "0 2px", color: "var(--side-title)" }}>Aujourd'hui</span>
        </div>
        <div className="text-[11.5px] mt-1 capitalize" style={{ color: "var(--side-muted)" }}>{todayStr}</div>
        <div className="text-[12.5px] mt-2" style={{ color: "var(--side-fg)" }}>
          <span className="mono">{today.length}</span> RDV
        </div>
        {upcoming && (
          <div className="mt-1 text-[12.5px]" style={{ color: "var(--side-fg)" }}>
            Prochain <span className="mono">{fmtTime(upcoming.date)}</span> ·{" "}
            <button data-testid="today-next-patient" onClick={() => go("/agenda")} className="font-medium hover:underline" style={{ color: "var(--accent)" }}>
              {upcoming.patient_nom || upcoming.titre || "RDV"}
            </button>
          </div>
        )}
      </div>

      {/* Paramètres + utilisateur */}
      <div className="space-y-1 pt-1" style={{ borderTop: "1px solid var(--side-border)" }}>
        <button onClick={() => go("/parametres")} data-testid="nav-parametres"
          className="w-full flex items-center gap-3 transition-colors text-[13.5px] mt-2"
          style={{
            padding: "9px 10px", borderRadius: "8px",
            background: loc.pathname.startsWith("/parametres") ? "var(--accent)" : "transparent",
            color: loc.pathname.startsWith("/parametres") ? "var(--ink)" : "var(--side-fg)",
            fontWeight: loc.pathname.startsWith("/parametres") ? 600 : 500,
          }}>
          <Settings className="w-4 h-4" />Paramètres
        </button>
        <div className="flex items-center gap-2 px-1 pt-2">
          <div className="w-[30px] h-[30px] rounded-full flex items-center justify-center text-[11px] font-semibold shrink-0"
            style={{ background: "var(--side-card)", color: "var(--side-fg)" }}>{initials}</div>
          <div className="min-w-0 flex-1">
            <div className="text-[12px] font-medium truncate" style={{ color: "var(--side-title)" }}>{psName || user?.name || "Praticien"}</div>
            <div className="text-[11px] truncate" style={{ color: "var(--side-muted)" }}>{user?.email}</div>
          </div>
          <button data-testid="logout-btn" onClick={() => { setOpen(false); attempt(null, () => { logout(); nav("/login"); }); }}
            className="sx-icon-btn shrink-0" style={{ color: "var(--side-muted)" }} title="Déconnexion">
            <LogOut className="w-4 h-4" />
          </button>
        </div>
        <div data-testid="app-version" className="px-1 pt-1 text-[10px]" style={{ color: "var(--side-muted)" }} title={`v${APP_VERSION}`}>
          v{APP_VERSION}
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen flex" style={{ background: "var(--bg-app)" }}>
      {/* Sidebar desktop */}
      <aside className="hidden md:block w-56 fixed inset-y-0 left-0 z-30" style={{ background: "var(--side-bg)" }}>{SidebarInner()}</aside>

      {/* Sidebar mobile */}
      {open && (
        <div className="md:hidden fixed inset-0 z-40 flex">
          <div className="w-56" style={{ background: "var(--side-bg)" }}>{SidebarInner()}</div>
          <div className="flex-1 bg-black/40" onClick={() => setOpen(false)} />
        </div>
      )}

      <div className="flex-1 md:ml-56 flex flex-col min-w-0">
        {/* En-tête */}
        <header ref={headerRef} className="sticky top-0 z-20" style={{ background: "var(--surface)", borderBottom: "1px solid var(--border)" }}>
          <div style={{ padding: "14px 28px 12px" }}>
            <div className="flex items-start justify-between gap-4">
              <div className="min-w-0">
                <div className="flex items-center gap-1.5 flex-wrap text-[12px]" style={{ color: "var(--muted-2)" }}>
                  <button className="md:hidden mr-1" onClick={() => setOpen(true)}><Menu className="w-5 h-5" style={{ color: "var(--muted)" }} /></button>
                  {base !== "/parametres" && trail.map((c, i) => (
                    <span key={i} className="flex items-center gap-1.5">
                      {i > 0 && <ChevronRight className="w-3 h-3" />}
                      {c.to && i < trail.length - 1
                        ? <button className="hover:opacity-70" onClick={() => go(c.to)}>{c.label}</button>
                        : <span style={{ color: "var(--muted)" }}>{c.label}</span>}
                    </span>
                  ))}
                </div>
                {base !== "/parametres" && !(base === "/tournees" && seg[1]) && (
                  <h1 className="text-[24px] font-bold tracking-[-0.01em] mt-0.5" style={{ color: "var(--ink)" }}>
                    <span className="sx-hl">{title}</span>
                  </h1>
                )}
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <div data-testid="global-search" onClick={() => window.dispatchEvent(new CustomEvent("open-command-palette"))} className="hidden lg:flex items-center gap-2 sx-search w-[260px] cursor-text">
                  <Search className="w-[15px] h-[15px]" style={{ color: "var(--muted-2)" }} />
                  <span className="flex-1 text-[13px]" style={{ color: "var(--muted-2)" }}>Rechercher…</span>
                  <span className="sx-kbd">⌘K</span>
                </div>
                <button data-testid="quick-create-rdv" onClick={() => openCreerRdv()} className="sx-btn-primary">
                  <Plus className="w-[15px] h-[15px]" />Créer RDV
                </button>
              </div>
            </div>
            {panelTabs && (
              <div className="flex gap-6 mt-3">
                {panelTabs.map((t) => (
                  <button key={t.to} data-testid={`ptab-${t.to.split("/").pop()}`} onClick={() => go(t.to)}
                    className="sx-tab" data-active={tabActive(t)}>{t.label}</button>
                ))}
              </div>
            )}
          </div>
        </header>

        <main className="flex-1 p-4 lg:p-7 sx-fade-in">
          <Outlet />
        </main>
      </div>
      <CommandPalette />
    </div>
  );
}

/* Ruban d'outils réutilisable façon Office */
export function Ribbon({ groups }) {
  return (
    <div className="sx-card px-3 py-2.5 mb-4 flex flex-wrap gap-4 items-center">
      {groups.map((g, i) => (
        <div key={i} className="flex flex-col">
          <span className="text-[11px] font-semibold uppercase tracking-wider mb-1 px-1" style={{ color: "var(--muted-2)", letterSpacing: ".06em" }}>{g.name}</span>
          <div className="flex items-center gap-1.5 flex-wrap">{g.children}</div>
        </div>
      ))}
    </div>
  );
}
